"use client";

import Link from "next/link";
import { FormEvent, useEffect, useState } from "react";
import { usePrivy } from "@privy-io/react-auth";
import PortalNavBar from "../../components/PortalNavBar";
import { getCurrentUser } from "../../lib/backend";

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:3000";

type Invitation = {
  id: number;
  email: string;
  inviteType: "BSP" | "EAH" | string;
  accepted: boolean;
  createdAt?: string;
};

export default function InviteParticipantPage() {
  const { ready, authenticated, getAccessToken, login } = usePrivy();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"BSP" | "EAH">("BSP");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  async function loadInvitations() {
    const response = await fetch(`${API_BASE_URL}/api/invitations`, { cache: "no-store" });
    if (!response.ok) throw new Error(`Unable to load invitations (${response.status}).`);
    const payload = await response.json();
    const items = Array.isArray(payload.invitations) ? payload.invitations : [];
    setInvitations(items.filter((item: Invitation) => item.inviteType === "BSP" || item.inviteType === "EAH"));
  }

  useEffect(() => {
    if (!ready || !authenticated) {
      setLoading(false);
      return;
    }

    async function loadPage() {
      try {
        setLoading(true);
        setError(null);
        const accessToken = await getAccessToken();
        if (!accessToken) throw new Error("Access token unavailable.");
        const account = await getCurrentUser(accessToken);
        if (account.user.platformRole !== "PSE") {
          throw new Error("Only an agreement owner can manage BSP and EAH invitations.");
        }
        await loadInvitations();
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : "Unable to load agreement invitations.");
      } finally {
        setLoading(false);
      }
    }

    void loadPage();
  }, [authenticated, getAccessToken, ready]);

  async function handleInvite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setStatus(null);
    if (!email.includes("@")) {
      setError("Enter a valid email address.");
      return;
    }

    try {
      setSending(true);
      const recipient = email.trim();
      const response = await fetch(`${API_BASE_URL}/api/invitations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: recipient, inviteType: role }),
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) throw new Error(payload.error ?? "Unable to create invitation.");
      setEmail("");
      setStatus(`${role} invitation created for ${recipient}.`);
      await loadInvitations();
    } catch (inviteError) {
      setError(inviteError instanceof Error ? inviteError.message : "Unable to create invitation.");
    } finally {
      setSending(false);
    }
  }

  const navLinks = [
    { href: "/dashboard", label: "Dashboard" },
    { href: "/account", label: "Account" },
    { href: "/accounts-breakdown", label: "Accounts Breakdown" },
    { href: "/revenue-history", label: "Revenue History" },
    { href: "/earnings-history", label: "Earnings History" },
    { href: "/sales-history", label: "Sales History" },
    { href: "/distribution-controls", label: "Distribution Controls" },
  ];

  if (!ready || !authenticated) {
    return <main className="min-h-screen bg-slate-950 p-8 text-white"><button type="button" onClick={login} className="rounded-xl bg-blue-600 px-5 py-3 font-semibold">Sign in to manage participants</button></main>;
  }

  return (
    <main className="min-h-screen bg-slate-950 text-white">
      <section className="mx-auto max-w-7xl px-4 py-12 md:px-8 md:py-16">
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div><div className="mb-2 inline-block rounded-full bg-blue-500/20 px-3 py-1 text-xs font-semibold text-blue-300">PSE Agreement Management</div><h1 className="text-4xl font-bold md:text-6xl">Invite Participants</h1><p className="mt-2 text-lg text-slate-300">Invite Business Service Providers and Essential Account Holders to your agreement.</p></div>
          <Link href="/participants" className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-medium text-slate-200 hover:border-slate-500">Manage agreement</Link>
        </div>
        <PortalNavBar links={navLinks} portalTitle="Participant Navigation" />
        {error && <div className="mb-6 rounded-2xl border border-red-500/50 bg-red-500/10 p-4 text-sm text-red-200">{error}</div>}
        {status && <div className="mb-6 rounded-2xl border border-emerald-500/50 bg-emerald-500/10 p-4 text-sm text-emerald-200">{status}</div>}
        <div className="mb-10 rounded-3xl border border-blue-500/30 bg-slate-900 p-6 md:p-8"><h2 className="mb-6 text-2xl font-bold">Create Invitation</h2><form onSubmit={handleInvite} className="grid gap-4 md:grid-cols-[1fr_180px_auto] md:items-end"><div><label className="mb-1.5 block text-sm font-medium text-slate-300" htmlFor="invite-email">Recipient Email Address</label><input id="invite-email" type="email" required value={email} onChange={(event) => setEmail(event.target.value)} placeholder="participant@company.com" className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm text-white outline-none focus:border-blue-500" /></div><div><label className="mb-1.5 block text-sm font-medium text-slate-300" htmlFor="invite-role">Agreement Role</label><select id="invite-role" value={role} onChange={(event) => setRole(event.target.value as "BSP" | "EAH")} className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm text-white outline-none focus:border-blue-500"><option value="BSP">BSP</option><option value="EAH">EAH</option></select></div><button type="submit" disabled={sending || loading} className="rounded-xl bg-blue-600 px-6 py-3 font-semibold text-white hover:bg-blue-500 disabled:opacity-50">{sending ? "Sending..." : "Send invitation"}</button></form></div>
        <div className="overflow-hidden rounded-3xl border border-slate-800 bg-slate-900"><div className="hidden bg-slate-800 p-5 font-semibold md:grid md:grid-cols-4"><div>Recipient</div><div>Role</div><div>Status</div><div>Date Sent</div></div>{loading ? <div className="p-8 text-center text-slate-400">Loading invitations...</div> : invitations.length === 0 ? <div className="p-8 text-center text-slate-400">No BSP or EAH invitations have been created.</div> : invitations.map((invitation) => <div key={invitation.id} className="grid gap-4 border-t border-slate-800 p-6 md:grid-cols-4 md:p-5"><div className="break-all font-medium text-white">{invitation.email}</div><div><span className="rounded-md bg-blue-500/20 px-2.5 py-1 text-xs font-semibold text-blue-300">{invitation.inviteType}</span></div><div><span className={`rounded-full px-3 py-1 text-xs font-semibold ${invitation.accepted ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>{invitation.accepted ? "Accepted" : "Pending"}</span></div><div className="text-sm text-slate-300">{invitation.createdAt ? new Date(invitation.createdAt).toLocaleDateString() : "Recent"}</div></div>)}</div>
      </section>
    </main>
  );
}